import { Link } from "react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { BarChart, TrendChart } from "../../components/charts";
import { ProvenanceNote } from "../../components/console";
import { DxCard, DxSection, KpiTile, RankList } from "../../components/dx";
import { ArrowRight } from "../../components/icons";
import { bucketDaily, fetchFees, fetchSummary, placeKey } from "../../lib/dashboard-data";
import { feeBarChart, moneyParts, moneyText } from "../../lib/dx";
import { fullLAK, num } from "../../lib/format";
import { dm, dmy, rangeKey, type Range } from "../../lib/stats-range";
import type { DashCtx } from "./ctx";

/** การ์ด 4 ใบบนสุดใช้ช่วงคงที่ ไม่ขึ้นกับตัวกรอง (mockup `dx_rev.js`) */
const FIXED: { range: Range; label: string; tone: "gold" | "green" | "navy" | "teal" }[] = [
  { range: { kind: "today" }, label: "ມື້ນີ້", tone: "gold" },
  { range: { kind: "days", days: 7 }, label: "7 ວັນຫຼ້າສຸດ", tone: "green" },
  { range: { kind: "days", days: 30 }, label: "30 ວັນຫຼ້າສຸດ", tone: "navy" },
  { range: { kind: "ytd" }, label: "ນັບແຕ່ຕົ້ນປີ (YTD)", tone: "teal" },
];

/**
 * ແຖບ ລາຍຮັບ — ค่าธรรมเนียมที่ชำระแล้ว นับตามวันที่จ่าย (paid_at) ทุกตัวเลขบนแท็บนี้
 *
 * mockup เรียก "ລາຍຮັບ (ມີໃບຮັບເງິນ)" — จอใช้ป้าย "ຄ່າທຳນຽມທີ່ຊຳລະແລ້ວ" ตามที่มาของตัวเลข (request_payments)
 */
export function RevenueTab({ ctx }: { ctx: DashCtx }) {
  const { range, place, here, data, unit } = ctx;
  const key = `${rangeKey(range)}|${placeKey(place)}`;
  const fixed = useQueries({
    queries: FIXED.map((f) => ({
      queryKey: ["stats", "summary", `${rangeKey(f.range)}|${placeKey(place)}`],
      queryFn: () => fetchSummary(f.range, place),
    })),
  });
  const feeUnit = data.weekly ? "month" : "week";
  const fees = useQuery({ queryKey: ["stats", "fees", key, feeUnit], queryFn: () => fetchFees(range, place, feeUnit) });
  const bar = feeBarChart(fees.data ?? [], feeUnit);
  const buckets = bucketDaily(data.daily.data ?? [], data.weekly);
  const s = data.summary.data;
  const rows = [...(unit?.rows ?? [])].filter((r) => r.declared_fee_lak > 0).sort((a, b) => b.declared_fee_lak - a.declared_fee_lak);
  const totalFee = s?.declared_fee_lak ?? 0;

  return (
    <>
      {fees.error ? <div className="alert-error dx-gap-b">ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {fees.error.message}</div> : null}
      <div className="dx-kpis dx-kpis--4">
        {FIXED.map((f, i) => {
          const q = fixed[i]?.data;
          const m = q ? moneyParts(q.declared_fee_lak) : undefined;
          return (
            <KpiTile
              key={f.label}
              label={`ຄ່າທຳນຽມ · ${f.label}`}
              value={m?.v}
              unit={m?.u}
              tone={f.tone}
              hint={q ? `${num(q.payments)} ລາຍການ · ບໍ່ຂຶ້ນກັບຊ່ວງເວລາທີ່ເລືອກ` : undefined}
            />
          );
        })}
      </div>

      <DxSection title="ຕາມຊ່ວງເວລາທີ່ເລືອກ" sub={`${here.name} · ${ctx.period}`} />
      <div className="dx-two">
        <DxCard
          title="ແນວໂນ້ມຄ່າທຳນຽມ"
          sub={data.weekly ? "ລວມເປັນລາຍອາທິດ" : "ລາຍວັນ ຕາມເວລາລາວ"}
          right={s ? <span className="dx-big num">{moneyText(s.declared_fee_lak)}</span> : undefined}
        >
          <TrendChart
            series={[{ label: "ຄ່າທຳນຽມທີ່ຊຳລະ", color: "var(--gold-600)", values: buckets.map((b) => b.row.fee_lak) }]}
            labels={buckets.map((b) => dm(b.start))}
            tooltipTitle={(i) => {
              const b = buckets[i];
              if (!b) return "";
              return b.start === b.end ? dmy(b.start) : `${dmy(b.start)} – ${dmy(b.end)}`;
            }}
            format={fullLAK}
            title="ກຣາຟແນວໂນ້ມຄ່າທຳນຽມ"
            emptyLabel={data.daily.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີການຊຳລະໃນຊ່ວງນີ້"}
          />
        </DxCard>
        <DxCard
          title={`ຄ່າທຳນຽມຕາມໝວດ (${feeUnit === "week" ? "ລາຍອາທິດ" : "ລາຍເດືອນ"})`}
          sub="ຊ່ວງທີ່ບໍ່ມີລາຍການສະແດງເປັນຄ່າສູນ ບໍ່ແມ່ນຂໍ້ມູນຂາດຫາຍ"
        >
          <BarChart
            series={bar.series}
            labels={bar.labels}
            title="ກຣາຟແທ່ງຄ່າທຳນຽມແຍກຕາມໝວດ"
            emptyLabel={fees.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີການຊຳລະໃນຊ່ວງນີ້"}
            format={fullLAK}
          />
        </DxCard>
      </div>

      {unit ? (
        <div className="dx-gap">
          <DxCard title={`ຈັດອັນດັບ${unit.word} ຕາມຄ່າທຳນຽມ`} sub={`ຫຼາຍ → ໜ້ອຍ${unit.onDrill ? " · ກົດແຖວເພື່ອເບິ່ງລາຍລະອຽດ" : ""}`}>
            <RankList
              emptyLabel={ctx.breakdown.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີການຊຳລະໃນຊ່ວງນີ້"}
              rows={rows.map((r) => ({
                key: r.place_id ?? "unplaced",
                label: unit.nameOf(r),
                sub: `${num(r.payments)} ລາຍການ`,
                value: r.declared_fee_lak,
                text: moneyText(r.declared_fee_lak),
                share: totalFee > 0 ? r.declared_fee_lak / totalFee : null,
                color: "var(--gold-500)",
                ...(unit.onDrill && r.place_id ? { onClick: () => unit.onDrill?.(r.place_id as string) } : {}),
              }))}
            />
            {s && rows.length > 0 ? (
              <p className="dx-note">
                ລວມ{here.name}: <span className="num">{fullLAK(s.declared_fee_lak)}</span> ຈາກ <span className="num">{num(s.payments)}</span> ລາຍການ
              </p>
            ) : null}
          </DxCard>
        </div>
      ) : null}

      <div className="dx-gap">
        <ProvenanceNote>
          ຄ່າທຳນຽມນັບຕາມວັນທີ່ຊຳລະ ຈາກການຈ່າຍຜ່ານ Lao QR ທີ່ທະນາຄານຢືນຢັນແລ້ວ ແລະ ການບັນທຶກຊຳລະຢູ່ຫ້ອງການ
        </ProvenanceNote>
        <Link to="/district/revenue" className="dx-sec__link">
          ເບິ່ງລາຍການຊຳລະທັງໝົດ <ArrowRight size={14} />
        </Link>
      </div>
    </>
  );
}
